import React from "react";
import Box from "../../../components/Box";
import styled from "styled-components";
import DriveTurnoverCard from "../../../components/DriveTurnoverCard";
import EcomBreakdownCard from "../../../components/EcomBreakdownCard";
import SelectOption from "../../../components/SelectOption";
import { useSearchParams } from "react-router-dom";
import { Choice } from "../../../types/commercial";
import { SingleValue } from "react-select";
import useSWR from "swr";
import { useStores } from "../../../context/storesContext";
import { fetcherWithStoreKey } from "../../../utils/fetcher";
import { getLastYearRange, Range } from "../../../utils/range";
import { EcomServiceType } from "../../../utils/ecomServiceType";
import { useOptionEcom, useOptionSlot } from "../../../utils/options";
import FlopDriveCard from "../../../components/FlopDriveCard";
import OrderStatusCard from "../../../components/OrderStatusCard";
import ComplianceRateCard from "../../../components/ComplianceRateCard";
import SlotsCard from "../../../components/SlotsCard";
import dayjs from "dayjs";
import {
  ComplianceRates,
  DriveSlotItem,
  DriveSlot as DriveSlotType,
} from "../../../types/drive";
import { eventTrack } from "../../../utils/gtag";
import { useTranslation } from "react-i18next";
import { useKpiVisibility } from "../../../kpis/useKpiVisibility";
import { useRange } from "../../../context/rangeContext";

const BoxContainer = styled(Box)`
  gap: ${(props) => `${props.theme.space?.[2]}px`};
`;

const isValidComplianceRate = (rate?: number | null) =>
  typeof rate === "number" && Number.isFinite(rate);

const hasComplianceData = (data?: ComplianceRates | null) =>
  !!data &&
  (isValidComplianceRate(data.beforeSubstitutionComplianceRate) ||
    isValidComplianceRate(data.afterSubstitutionComplianceRate));

function DrivePage() {
  const { selectedStore } = useStores();
  const [searchParams, setSearchParams] = useSearchParams();
  const optionEcom = useOptionEcom();
  const {t} = useTranslation();
  const { getKpiRange, setKpiRange } = useRange();

  const getDefaultValueParams = () => {
    const value = getKpiRange("turnover_drive") ?? optionEcom?.[0]?.value as string;
    searchParams.set("range", value);
    setSearchParams(searchParams);
    // first tracking
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: t('commercial.chiffre_affaires'),
      key2: "filter_name",
      value2: value,
    });
    return value;
  };

  const range = searchParams.get("range") ?? getDefaultValueParams();
  const handleOption = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === range) {
      return;
    }
    searchParams.set("range", newValue as string);
    setSearchParams(searchParams);
    setKpiRange("turnover_drive", newValue as Range);
    // ttracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: t('commercial.chiffre_affaires'),
      key2: "filter_name",
      value2: newValue,
    });
  };

  // current total e-commerce turnover (all service types, broken down)
  const { data: currentTotalTurnoverData, isLoading: isLoadingCurrentTotal } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/turnover?range=${
              range
            }&ecomServiceType=${EcomServiceType.EcomTotal}`,

            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // past total e-commerce turnover (all service types, broken down)
  const { data: pastTotalTurnoverData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/turnover?range=${
              getLastYearRange(range)
            }&ecomServiceType=${EcomServiceType.EcomTotal}`,

            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );

  // Drive Flop
  const { data: driveFlopData, error: driveFlopError } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/flop`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  
  // Orders Status
  //DIRVE
  const { data: ordersDriveData, error: ordersDriveError } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/orders?ecomServiceType=${EcomServiceType.Drive}&range=${Range.Direct}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  //LAD
  const { data: ordersLadData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/orders?ecomServiceType=${EcomServiceType.HomeDelivery}&range=${Range.Direct}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // Taux comformité
  //DRIVE
  const {
    data: complianceDriveData,
    error: complianceDriveError,
    isLoading: isComplianceDriveLoading,
  } = useSWR<ComplianceRates | null>(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/compliance?ecomServiceType=${EcomServiceType.Drive}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  //LAD
  const {
    data: complianceLadData,
    error: complianceLadError,
    isLoading: isComplianceLadLoading,
  } = useSWR<ComplianceRates | null>(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/compliance?ecomServiceType=${EcomServiceType.HomeDelivery}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // current Drive Objective
  const { data: currentDriveObjectiveData } = useSWR(
    () =>
      selectedStore?.storeKey
        ? [
            `/drive/objective?range=${
              range
            }&ecomServiceType=${EcomServiceType.Drive}`,
            
            selectedStore?.storeKey as string,
          ]
        : null,
    ([url, storeEan]) =>
      fetcherWithStoreKey(url,  storeEan as string)
  );
  // Today Drive slot
  const { data: slotTodayDriveData, error: slotTodayDriveError } =
    useSWR<DriveSlotType>(
      () =>
        selectedStore?.storeKey
          ? [
              `/drive/slot?day=${Range.Today}&ecomServiceType=${EcomServiceType.Drive}`,
              
              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url,  storeEan as string)
    );
  // Today LAD slot
  const { data: slotTodayLadData, error: slotTodayLadError } =
    useSWR<DriveSlotType>(
      () =>
        selectedStore?.storeKey
          ? [
              `/drive/slot?day=${Range.Today}&ecomServiceType=${EcomServiceType.HomeDelivery}`,
              
              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url,  storeEan as string)
    );
  // Nextday DRIVE slot
  const { data: slotNextdayDriveData, error: slotNextdayDriveError } =
    useSWR<DriveSlotType>(
      () =>
        selectedStore?.storeKey
          ? [
              `/drive/slot?day=${Range.NextDay}&ecomServiceType=${EcomServiceType.Drive}`,
              
              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url,  storeEan as string)
    );

  // Nextday LAD slot
  const { data: slotNextdayLadData, error: slotNextdayLadError } =
    useSWR<DriveSlotType>(
      () =>
        selectedStore?.storeKey
          ? [
              `/drive/slot?day=${Range.NextDay}&ecomServiceType=${EcomServiceType.HomeDelivery}`,
              
              selectedStore?.storeKey as string,
            ]
          : null,
      ([url, storeEan]) =>
        fetcherWithStoreKey(url,  storeEan as string)
    );

  const optionSlot = useOptionSlot();
  const {isKpiExists} = useKpiVisibility();
  const hasDriveComplianceData = hasComplianceData(complianceDriveData);
  const hasLadComplianceData = hasComplianceData(complianceLadData);
  const isComplianceLoading =
    !hasDriveComplianceData &&
    !hasLadComplianceData &&
    (isComplianceDriveLoading || isComplianceLadLoading);
  const isComplianceUnavailable =
    !isComplianceLoading && !hasDriveComplianceData && !hasLadComplianceData;

  // {"maxCapacitySlot":28,"slotDetails":null}

  return (
    <BoxContainer
      width={"100%"}
      display={"flex"}
      flexDirection={"column"}
      justifyContent={"center"}
    >
      {isKpiExists("Ecommerce", "DriveTurnover") && (
        <Box display={"flex"} justifyContent={"flex-start"} width={"100%"}>
          <SelectOption
            list={optionEcom}
            value={range}
            handleChange={handleOption}
          />
        </Box>
      )}
      {isKpiExists("Ecommerce", "DriveTurnover") && (
        <DriveTurnoverCard
          title={t('ecommerce.ecommerce')}
          currentTotalData={currentTotalTurnoverData}
          pastTotalData={pastTotalTurnoverData}
          driveObjective={currentDriveObjectiveData}
          isLoadingTotal={false}
          isUnavailableTotal={false}
          openInformationModal={() => null}
        />
      )}
      {(isKpiExists("Ecommerce", "DriveTurnover") || isKpiExists("Ecommerce", "ComplianceRate")) && (
        <Box display={"flex"} flexWrap={"wrap"} style={{ gap: "16px" }} width={"100%"}>
          {isKpiExists("Ecommerce", "DriveTurnover") && (
            <Box style={{ flex: "1 1 380px", display: "flex" }}>
              <EcomBreakdownCard
                width={"100%"}
                items={currentTotalTurnoverData?.items}
                pastItems={pastTotalTurnoverData?.items}
                importedDateTime={currentTotalTurnoverData?.importedDateTime}
                isLoading={isLoadingCurrentTotal}
                isUnavailable={false}
                openInformationModal={() => null}
              />
            </Box>
          )}
          {isKpiExists("Ecommerce", "ComplianceRate") && (
            <Box style={{ flex: "1 1 380px", display: "flex" }}>
              <ComplianceRateCard
                width={"100%"}
                isLoading={isComplianceLoading}
                isUnavailable={
                  isComplianceUnavailable ||
                  (!!complianceDriveError && !!complianceLadError)
                }
                openInformationModal={() => null}
                driveComplianceData={
                  hasDriveComplianceData ? complianceDriveData : undefined
                }
                ladComplianceData={
                  hasLadComplianceData ? complianceLadData : undefined
                }
              />
            </Box>
          )}
        </Box>
      )}
      {isKpiExists("Ecommerce", "OrderStatus") && (
        <OrderStatusCard
          isLoading={!ordersDriveData}
          isUnavailable={!!ordersDriveError || ordersDriveData === null || ordersDriveData?.delivered === null}
          openInformationModal={() => null}
          orderDriveData={ordersDriveData}
          orderLadData={ordersLadData}
        />
      )}
      {isKpiExists("Ecommerce", "FlopDrive") && (
        <FlopDriveCard
          isLoading={!driveFlopData}
          isUnavailable={!!driveFlopError || driveFlopData === null || driveFlopData?.[0]?.barcode === null}
          listValue={driveFlopData}
          openInformationModal={() => null}
        />
      )}
      {isKpiExists("Ecommerce", "Slots") && (
        <SlotsCard
          title={t('ecommerce.occupation_creneaux')}
          driveTodaySlots={{
            maxCapacitySlot: slotTodayDriveData?.maxCapacitySlot as number,
            slotsDetail: (slotTodayDriveData?.slotsDetail || []).map((slot) => ({
              ...slot,
              startDateTime: dayjs(slot.startDateTime),
            })) as Array<DriveSlotItem>,
          }}
          driveNextDaySlots={{
            maxCapacitySlot: slotNextdayDriveData?.maxCapacitySlot as number,
            slotsDetail: (slotNextdayDriveData?.slotsDetail || []).map(
              (slot) => ({
                ...slot,
                startDateTime: dayjs(slot.startDateTime),
              })
            ) as Array<DriveSlotItem>,
          }}
          ladTodaySlots={{
            maxCapacitySlot: slotTodayLadData?.maxCapacitySlot as number,
            slotsDetail: (slotTodayLadData?.slotsDetail || []).map((slot) => ({
              ...slot,
              startDateTime: dayjs(slot.startDateTime),
            })) as Array<DriveSlotItem>,
          }}
          ladNextDaySlots={{
            maxCapacitySlot: slotNextdayLadData?.maxCapacitySlot as number,
            slotsDetail: (slotNextdayLadData?.slotsDetail || []).map((slot) => ({
              ...slot,
              startDateTime: dayjs(slot.startDateTime),
            })) as Array<DriveSlotItem>,
          }}  
          importedDateTime={
            slotTodayDriveData?.importedDateTime ||
            slotNextdayDriveData?.importedDateTime ||
            slotTodayLadData?.importedDateTime ||
            slotNextdayLadData?.importedDateTime
          }
          range={"slot"}
          options={optionSlot}
          openInformationModal={() => null}
          isLoading={
            !slotTodayDriveData &&
            !slotNextdayDriveData &&
            !slotTodayLadData &&
            !slotNextdayLadData
          }
          isUnavailable={
            (!!slotTodayDriveError &&
            !!slotNextdayDriveError &&
            !!slotTodayLadError &&
            !!slotNextdayLadError)
            || 
            slotTodayDriveData?.slotsDetail === null
          }
        />
      )}
      
    </BoxContainer>
  );
}

export default DrivePage;
